import { useState } from 'react';
import { Search, AlertCircle, CheckCircle, RotateCcw } from 'lucide-react';
import Header from "../../../components/Header/Header";
import Footer from "../../../components/Footer/Footer";
import { useSelector } from 'react-redux';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function RevertIndent() {
  const [indentId, setIndentId] = useState('');
  const [indentDetails, setIndentDetails] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  // No issues with this line - it correctly destructures roleid from the auth state using Redux's useSelector hook
const roleid = useSelector((state) => state.auth.id);

  const handleSearch = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/adminpage/indent/${indentId}`);
      const data = await response.json();
      
      if (response.ok) {
        setIndentDetails(data);
        setError('');
      } else {
        setError(data.error || 'Indent not found');
        setIndentDetails(null);
      }
    } catch (err) {
      setError('Failed to fetch indent details');
      setIndentDetails(null);
    }
  };

  const handleRevert = async (level) => {
    try {
      const response = await fetch(`${API_BASE_URL}/adminpage/indent/revert/${indentId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          revertTo: level,
          role: roleid 
        }),
      });

      if (response.ok) {
        setSuccess(`Successfully reverted indent status to ${level} approval stage`);
        handleSearch(); // Refresh indent details
      } else {
        setError('Failed to revert indent status');
      }
    } catch (err) {
      setError('Failed to revert indent status');
    }
  };

  const getLevelStatus = (indent) => {
    if (!indent) return [];
    
    return [
      { name: 'PE', status: indent.L1A === 1 ? 'Approved' : 'Pending' },
      { name: 'L1', status: indent.L1 === 1 ? 'Approved' : 'Pending' },
      { name: 'L2', status: indent.L2 === 1 ? 'Approved' : 'Pending' },
      { name: 'PD', status: indent.PO === 1 ? 'Approved' : 'Pending' },
      { name: 'PH', status: indent.PH === 1 ? 'Approved' : 'Pending' },
      { name: 'SO', status: indent.IR === 1 ? 'Approved' : 'Pending' },
      { name: 'SH', status: indent.ForceClosureStatus === 1 ? 'Approved' : 'Pending' }
    ];
  };

  // Get approved levels that can be reverted to
  const getRevertLevels = (indent) => {
    if (!indent) return [];
    
    // Define the approval chain order
    const approvalChain = ['L1A', 'L1', 'L2', 'PO', 'PH', 'IR'];
    const levels = [];
    
    // Find the current highest approved level
    let highestApprovedIndex = -1;
    for (let i = approvalChain.length - 1; i >= 0; i--) {
      const level = approvalChain[i];
      if (indent[level] === 1) {
        highestApprovedIndex = i;
        break;
      }
    }

    // Add all approved levels before and including the current highest level
    for (let i = 0; i <= highestApprovedIndex; i++) {
      const level = approvalChain[i];
      if (indent[level] === 1) {
        levels.push(level);
      }
    }
    
    return levels;
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />
      <div className="p-6">
        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-bold mb-6">Revert Indent Status</h2>
            
            <div className="flex gap-4 mb-6">
              <div className="relative flex-grow">
                <input
                  type="text"
                  value={indentId}
                  onChange={(e) => setIndentId(e.target.value)}
                  placeholder="Enter Indent ID"
                  className="w-full p-2 pr-10 border rounded-lg focus:outline-none focus:border-blue-500"
                />
                <Search className="absolute right-3 top-2.5 text-gray-400" size={20} />
              </div>
              <button
                onClick={handleSearch}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <Search size={20} />
                Search
              </button>
            </div>

            {error && (
              <div className="flex items-start gap-2 p-4 mb-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                <AlertCircle className="mt-0.5 flex-shrink-0" size={20} />
                <span>{error}</span>
              </div>
            )}

            {success && (
              <div className="flex items-start gap-2 p-4 mb-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
                <CheckCircle className="mt-0.5 flex-shrink-0" size={20} />
                <span>{success}</span>
              </div>
            )}

            {indentDetails && (
              <div className="space-y-6">
                <div className="border rounded-lg p-4">
                  <h3 className="font-semibold mb-4">Current Status:</h3>
                  <div className="grid grid-cols-2 gap-4">
                    {getLevelStatus(indentDetails).map((level) => (
                      <div key={level.name} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                        <span className="font-medium">{level.name}</span>
                        <span className={`px-3 py-1 rounded-full text-sm ${
                          level.status === 'Approved' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {level.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="border rounded-lg p-4">
                  <h3 className="font-semibold mb-4">Revert to:</h3>
                  <div className="grid grid-cols-2 gap-4">
                    {getRevertLevels(indentDetails).map((level) => (
                      <button
                        key={level}
                        onClick={() => handleRevert(level)}
                        className="flex items-center justify-center gap-2 bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700 transition-colors"
                      >
                        <RotateCcw size={18} />
                        Revert to {level}
                      </button>
                    ))}
                    {getRevertLevels(indentDetails).length === 0 && (
                      <div className="col-span-2 text-center text-gray-500">
                        No revert options available
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default RevertIndent;